import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, Spinner } from '@structural-climbing/ui'

import { useGymThemeContext } from '../layouts/useGymTheme'
import { CheckInActions } from '../features/members/CheckInActions'
import { MemberIdentity } from '../features/members/MemberIdentity'
import { MemberProfileModal } from '../features/members/MemberProfileModal'
import { SubBadge } from '../features/members/SubBadge'
import { useMyTrainees } from '../features/trainers/useMyTrainees'
import { TrainersOverview } from '../features/trainers/TrainersOverview'
import { useCheckIn } from '../features/checkIn/useCheckIn'
import { useCheckOut } from '../features/checkIn/useCheckOut'
import { PageHeader } from '../components/PageHeader'

/**
 * A trainer's own caseload. Deliberately not a copy of MembersPage: there are no
 * status tabs, no search and no plan actions, because a trainer has a handful of
 * people and no authority over anyone else's membership.
 *
 * The chrome, though, is the same as every other gym page — `p-4 sm:p-8` page
 * padding, title and subtitle above the card, `divide-y` rows at `py-3`, and the
 * indented child row from MembersPage — so it does not read as a page from a
 * different app.
 */
export function MyMembersPage() {
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const {
    trainees,
    upcoming,
    self,
    loading,
    error,
    isTrainer,
    reload,
    checkedInMap,
    childCheckedInMap,
  } = useMyTrainees()
  const [profileTarget, setProfileTarget] = useState<string | null>(null)
  const { checkIn, stateFor } = useCheckIn()
  const { checkOut, isCheckingOut, isAlreadyCheckedOut } = useCheckOut(reload)

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        title={t('myMembers.title')}
        subtitle={isTrainer === false ? t('myMembers.ownerSubtitle') : t('myMembers.subtitle')}
      />

      {/* The trainer climbs here too. Without this they could only get in
          through an assignment to themselves, which put them in their own
          caseload. */}
      {isTrainer === true && self && (
        <Card className="mb-4">
          <CardBody>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40">
              {t('myMembers.selfTitle')}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <MemberIdentity
                userId={self.user_id}
                name={self.user_name}
                nickname={self.nickname}
                email={self.user_email}
                avatarExt={self.avatar_ext}
                avatarUpdatedAt={self.avatar_updated_at}
                primaryColor={primaryColor}
                isClubMember={self.flags.is_club_member === true}
              />
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                <SubBadge status={self.subscription?.effective_status} />
                <CheckInActions
                  userId={self.user_id}
                  subStatus={self.subscription?.effective_status}
                  checkInState={stateFor(self.user_id)}
                  activeEntry={checkedInMap.get(self.user_id)}
                  isCheckingOut={isCheckingOut}
                  isAlreadyCheckedOut={isAlreadyCheckedOut}
                  onCheckIn={(userId) => void checkIn(userId)}
                  onCheckOut={(id, isGuest) => void checkOut(id, isGuest)}
                />
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody>
          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="sm" color="primary" />
            </div>
          ) : isTrainer === false ? (
            <TrainersOverview />
          ) : error ? (
            <p className="py-8 text-center text-sm text-danger">{t('myMembers.errorLoad')}</p>
          ) : trainees.length === 0 ? (
            <p className="py-8 text-center text-sm text-foreground/40">{t('myMembers.empty')}</p>
          ) : (
            <div className="flex flex-col divide-y divide-divider">
              {trainees.map((trainee) => (
                <div key={trainee.user_id}>
                  <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <button
                      type="button"
                      className="flex w-full min-w-0 cursor-pointer items-center gap-3 text-left sm:flex-1"
                      title={t('members.viewProfile')}
                      onClick={() => setProfileTarget(trainee.user_id)}
                    >
                      <MemberIdentity
                        userId={trainee.user_id}
                        name={trainee.user_name}
                        nickname={trainee.nickname}
                        email={trainee.user_email}
                        avatarExt={trainee.avatar_ext}
                        avatarUpdatedAt={trainee.avatar_updated_at}
                        primaryColor={primaryColor}
                        isClubMember={trainee.flags.is_club_member === true}
                      />
                    </button>
                    {/* Only the adult's own badge. A parent listed solely because
                        their child is a trainee is a heading here, not a member
                        of this trainer's caseload. */}
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                      {trainee.assigned && (
                        <>
                          <SubBadge status={trainee.subscription?.effective_status} />
                          <CheckInActions
                            userId={trainee.user_id}
                            subStatus={trainee.subscription?.effective_status}
                            checkInState={stateFor(trainee.user_id)}
                            activeEntry={checkedInMap.get(trainee.user_id)}
                            isCheckingOut={isCheckingOut}
                            isAlreadyCheckedOut={isAlreadyCheckedOut}
                            onCheckIn={(userId) => void checkIn(userId)}
                            onCheckOut={(id, isGuest) => void checkOut(id, isGuest)}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {trainee.children.length > 0 && (
                    <div className="mb-3 ml-4 flex flex-col gap-1.5">
                      {trainee.children.map((child) => (
                        <div
                          key={child.id}
                          className="flex flex-col gap-2 rounded-lg bg-content2/50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="text-xs text-foreground/30">└</span>
                            <p className="truncate text-sm">
                              {[child.first_name, child.last_name].filter(Boolean).join(' ')}
                            </p>
                            <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                              {t('members.childBadge')}
                            </span>
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                            <SubBadge status={child.subscription?.effective_status} />
                            <CheckInActions
                              userId={trainee.user_id}
                              childId={child.id}
                              subStatus={child.subscription?.effective_status}
                              checkInState={stateFor(trainee.user_id, child.id)}
                              activeEntry={childCheckedInMap.get(child.id)}
                              isCheckingOut={isCheckingOut}
                              isAlreadyCheckedOut={isAlreadyCheckedOut}
                              onCheckIn={(uid, cid) => void checkIn(uid, cid)}
                              onCheckOut={(id) => void checkOut(id, false)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Blocks that have not started yet. Separate on purpose: they carry
              no check-in button, because the server refuses a check-in before
              the assignment's start date. */}
          {isTrainer !== false && upcoming.length > 0 && (
            <div className="mt-6">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40">
                {t('myMembers.futureTitle')}
              </p>
              <div className="flex flex-col divide-y divide-divider">
                {upcoming.map((trainee) => (
                  <div
                    key={`up-${trainee.user_id}`}
                    className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                  >
                    <button
                      type="button"
                      className="flex w-full min-w-0 cursor-pointer items-center gap-3 text-left sm:flex-1"
                      title={t('members.viewProfile')}
                      onClick={() => setProfileTarget(trainee.user_id)}
                    >
                      <MemberIdentity
                        userId={trainee.user_id}
                        name={trainee.user_name}
                        nickname={trainee.nickname}
                        email={trainee.user_email}
                        avatarExt={trainee.avatar_ext}
                        avatarUpdatedAt={trainee.avatar_updated_at}
                        primaryColor={primaryColor}
                      />
                    </button>
                    <span className="shrink-0 text-xs text-foreground/50">
                      {t('myMembers.startsOn', {
                        date: trainee.assigned
                          ? (trainee.starts_at ?? '')
                          : (trainee.children[0]?.starts_at ?? ''),
                      })}
                      {!trainee.assigned && trainee.children[0]
                        ? ` · ${[trainee.children[0].first_name, trainee.children[0].last_name].filter(Boolean).join(' ')}`
                        : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {profileTarget && (
        <MemberProfileModal userId={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </div>
  )
}
